import { useMutation } from "@apollo/client";
import { BULK_UPLOAD_ASSETS } from "../../graphql/mutations/assets/assets.mutations";

// Sent in chunks (rather than one giant request) so the caller can report
// real progress as each chunk resolves, instead of one opaque wait for
// however long the entire batch takes.
const CHUNK_SIZE = 50;

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const useBulkUploadAssets = () => {
  const [bulkUploadAssetsMutation, { loading, error }] =
    useMutation(BULK_UPLOAD_ASSETS);

  // onProgress(processedCount, totalCount) is called after each chunk resolves.
  // `resumeFrom` is a previous partial result ({ created, failed }) — pass it
  // back in on retry to continue from where a mid-way failure left off,
  // instead of resubmitting rows that already succeeded.
  const bulkUploadAssets = async (assets, onProgress, resumeFrom) => {
    const created = [...(resumeFrom?.created ?? [])];
    const failed = [...(resumeFrom?.failed ?? [])];
    const remaining = assets.slice(created.length + failed.length);

    try {
      for (const batch of chunkArray(remaining, CHUNK_SIZE)) {
        const res = await bulkUploadAssetsMutation({ variables: { assets: batch } });
        const result = res?.data?.bulkUploadAssets ?? { created: [], failed: [] };
        created.push(...(result.created ?? []));
        failed.push(...(result.failed ?? []));
        onProgress?.(created.length + failed.length, assets.length);
      }
      return { created, failed };
    } catch (err) {
      console.error(`Error uploading assets: ${err?.message}`);
      const resumableError = new Error(err?.message || "Upload failed");
      resumableError.partialResult = { created, failed };
      throw resumableError;
    }
  };

  return { bulkUploadAssets, loading, error };
};

export default useBulkUploadAssets;
